
import React, { useState } from 'react';
import { Form, Button, Container, Card, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { FaLock, FaEnvelope, FaShieldAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import { login } from '../services/auth';
import axios from 'axios';

const AdminLogin = () => {
  const [admin_email, setEmail] = useState('');
  const [admin_password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const login_time = new Date().toISOString();
    const API_BASE = "https://api.collegeall.com/api/v1";

    try {
      const response = await axios.post(
        `${API_BASE}/admin/login`,
        {
          email: admin_email,
          password: admin_password
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = response.data;

      if (data.status === 200) {
        localStorage.setItem('adminToken', data.data.authToken);
        localStorage.setItem('api_key', data.data.api_key);
        localStorage.setItem('login_time', login_time);

        try {
          const decoded = jwtDecode(data.data.authToken);
          let role = 'Admin';

          login({
            email: admin_email,
            role,
            password: admin_password,
            name: decoded?.name || admin_email.split('@')[0]
          });

          navigate('/', { replace: true });
        } catch (decodeError) {
          setError('Invalid authentication token received.');
        }
      } else {
        setError(data.message || 'Invalid email or password');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Server connection failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root min-vh-100 d-flex align-items-center justify-content-center">
      <div className="login-bg-blobs">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
      </div>
      
      <Container>
        <div className="row justify-content-center">
          <div className="col-11 col-sm-8 col-md-6 col-lg-4">
            <div className="text-center mb-4 animate-fade-down">
              <div className="logo-container mb-3 shadow-sm">
                <img src="https://collegeall.com/img/Logo/collegeallLogo.png" alt="collegeall Logo" className="login-logo" />
              </div>
              <h3 className="fw-bold text-slate-900 mb-1">SuperAdmin Access</h3>
              <p className="text-muted tiny-text uppercase tracking-widest fw-semibold">Secure Administrative Gateway</p>
            </div>

            <Card className="border-0 shadow-2xl login-card overflow-hidden animate-fade-up">
              <div className="login-card-accent"></div>
              <Card.Body className="p-4 p-md-5">
                {error && (
                  <Alert variant="danger" className="py-2 px-3 border-0 tiny-text fw-semibold mb-4 d-flex align-items-center rounded-3">
                    <FaShieldAlt className="me-2" /> {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="tiny-label fw-bold text-muted mb-2">EMAIL ADDRESS</Form.Label>
                    <div className="input-group-modern">
                      <FaEnvelope className="input-icon" />
                      <Form.Control
                        type="email"
                        value={admin_email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="admin@platform.com"
                        className="modern-input shadow-none"
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <div className="d-flex justify-content-between">
                      <Form.Label className="tiny-label fw-bold text-muted mb-2">PASSWORD</Form.Label>
                    </div>
                    <div className="input-group-modern">
                      <FaLock className="input-icon" />
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        value={admin_password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="••••••••"
                        className="modern-input shadow-none pe-5"
                      />
                      <button 
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </Form.Group>

                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={loading}
                    className="w-100 py-2 fw-bold shadow-sm btn-login d-flex align-items-center justify-content-center gap-2"
                  >
                    {loading ? (
                      <><Spinner animation="border" size="sm" /> VERIFYING...</>
                    ) : (
                      'AUTHORIZE ACCESS'
                    )}
                  </Button>
                </Form>
              </Card.Body>
              <div className="card-footer bg-slate-50/50 border-0 p-3 text-center">
                <span className="tiny-text text-muted fw-medium">Stable Version 2.5.0</span>
              </div>
            </Card>

            <div className="text-center mt-4 opacity-50">
              <p className="tiny-text text-slate-500">
                &copy; 2026 CareerConnect Enterprise Operations.
              </p>
            </div>
          </div>
        </div>
      </Container>

      <style>{`
        .login-root {
          background-color: #f8fafc;
          position: relative;
          overflow: hidden;
        }

        .login-bg-blobs {
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .blob {
          position: absolute;
          width: 400px;
          height: 400px;
          background: rgba(99, 102, 241, 0.08);
          border-radius: 50%;
          filter: blur(80px);
        }

        .blob-1 { top: -100px; right: -100px; background: rgba(99, 102, 241, 0.12); }
        .blob-2 { bottom: -100px; left: -100px; background: rgba(168, 85, 247, 0.08); }

        .logo-container {
          display: inline-flex;
          background: white;
          padding: 10px;
          border-radius: 16px;
        }

        .login-logo {
          height: 38px;
        }

        .login-card {
          border-radius: 1.5rem !important;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          z-index: 1;
        }

        .login-card-accent {
          height: 5px;
          background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
          width: 100%;
        }

        .tiny-label {
          font-size: 0.65rem;
          letter-spacing: 0.12em;
        }

        .tiny-text {
          font-size: 0.72rem;
        }

        .text-indigo-600 { color: #4f46e5; }
        .uppercase { text-transform: uppercase; }
        .tracking-widest { letter-spacing: 0.1em; }

        .input-group-modern {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          color: #94a3b8;
          font-size: 0.9rem;
          z-index: 10;
        }

        .modern-input {
          padding-left: 2.75rem !important;
          padding-top: 0.75rem !important;
          padding-bottom: 0.75rem !important;
          border-radius: 0.85rem !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 0.9rem !important;
          background: rgba(251, 252, 253, 0.6) !important;
          transition: all 0.2s ease-in-out !important;
        }

        .modern-input:focus {
          background: #ffffff !important;
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important;
        }

        .password-toggle-btn {
          position: absolute;
          right: 0.75rem;
          background: transparent;
          border: none;
          color: #94a3b8;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 11;
          transition: color 0.2s;
        }

        .password-toggle-btn:hover {
          color: #6366f1;
        }

        .btn-login {
          background: #6366f1;
          border: none;
          border-radius: 0.85rem;
          padding: 0.75rem !important;
          font-size: 0.85rem;
          letter-spacing: 0.05em;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .btn-login:hover:not(:disabled) {
          background: #4f46e5;
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3);
        }

        .btn-login:active {
          transform: translateY(0);
        }

        .animate-fade-up {
          animation: fadeUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }

        .animate-fade-down {
          animation: fadeDown 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .bg-slate-50\/50 { background-color: rgba(248, 250, 252, 0.5); }
        .text-slate-900 { color: #0f172a; }
        .shadow-2xl { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1); }
      `}</style>
    </div>
  );
};

export default AdminLogin;
